import { createRoot, type Root } from 'react-dom/client'
import { shadowLogger } from '../utils/logger'
import { ShadowCSSManager } from './css-manager'
import { ShadowContentDetector } from './content-detector'
import type { 
  ShadowEmbedConfig, 
  ShadowMountOptions, 
  ShadowInitResult 
} from './types'
import { isMobileDevice } from '../utils/mobile'

/**
 * Core Shadow DOM manager for production SDK
 * Handles Shadow DOM creation, CSS injection, and React app mounting
 */
export class ShadowManager {
  private shadowRoot: ShadowRoot | null = null
  private container: HTMLElement | null = null
  private reactRoot: Root | null = null
  private config: ShadowEmbedConfig
  private mounted = false
  private onResize?: (height: number) => void
  private onOpenLink?: (url: string) => void
  private onCenterRequest?: (height: number) => void

  constructor(
    config: ShadowEmbedConfig,
    callbacks?: {
      onResize?: (height: number) => void
      onOpenLink?: (url: string) => void
      onCenterRequest?: (height: number) => void
    }
  ) {
    this.config = config
    this.onResize = callbacks?.onResize
    this.onOpenLink = callbacks?.onOpenLink
    this.onCenterRequest = callbacks?.onCenterRequest
    
    shadowLogger.info('ShadowManager initialized', { 
      projectId: config.projectId,
      mountStrategy: config.mountStrategy,
      hasResizeCallback: !!this.onResize,
      hasLinkCallback: !!this.onOpenLink
    })
  }

  /**
   * Initialize Shadow DOM and prepare for mounting
   */
  async init(): Promise<ShadowInitResult> {
    try {
      // Find or create container
      const container = await this.findMountPoint()
      if (!container) {
        throw new Error('No suitable mount point found')
      }

      // Create Shadow DOM
      const shadowRoot = container.attachShadow({ 
        mode: this.config.shadowMode || 'open' 
      })

      // Store references
      this.container = container
      this.shadowRoot = shadowRoot

      // Inject CSS styles using ShadowCSSManager
      await ShadowCSSManager.injectStyles(shadowRoot, this.config.customCSS)

      return {
        shadowRoot,
        container,
        success: true
      }

    } catch (error) {
      const err = error as Error
      shadowLogger.error('❌ Shadow DOM initialization failed:', err)
      
      return {
        shadowRoot: null as any,
        container: null as any,
        success: false,
        error: err
      }
    }
  }

  /**
   * Get current configuration
   */
  getConfig(): ShadowEmbedConfig {
    return this.config
  }

  /**
   * Get the container element
   */
  getContainer(): HTMLElement | null {
    return this.container
  }

  /**
   * Handle resize requests from Shadow DOM content
   */
  private handleResize = (newHeight: number) => {
    if (!this.container) {
      shadowLogger.warn('Cannot resize: no container available')
      return
    }
    
    // Check if we're in mobile floating mode
    const isMobile = isMobileDevice()
    const isFloating = isMobile && this.config.mobileFloatingBar
    
    if (isFloating) {
      // In floating mode, don't set explicit height - let CSS constraints handle it
      shadowLogger.debug(`Mobile floating mode - height managed by CSS constraints (requested: ${newHeight}px)`)
      
      // Still call the callback for analytics/tracking purposes
      if (this.onResize) {
        this.onResize(newHeight)
      }
      return
    }

    // Regular embedded mode - update container height directly
    const oldHeight = this.container.style.height
    this.container.style.height = `${newHeight}px`
    
    shadowLogger.debug(`Resized Shadow DOM container from ${oldHeight} to ${newHeight}px`)
    
    // Call external callback if provided (for additional parent handling)
    if (this.onResize) {
      this.onResize(newHeight)
    }
  }

  /**
   * Handle center requests from Shadow DOM content
   */
  private handleCenterRequest = (newHeight: number) => {
    if (!this.container) {
      shadowLogger.warn('Cannot center: no container available')
      return
    }

    // Calculate the center position for Shadow DOM container
    const viewportHeight = window.innerHeight
    const containerHeight = newHeight
    
    // Get container position relative to viewport
    const containerRect = this.container.getBoundingClientRect()
    const containerTop = containerRect.top + window.scrollY
    
    // Calculate where to scroll to center the container
    const targetScrollY = containerTop - (viewportHeight - containerHeight) / 2
    
    // Smooth scroll to center the container
    window.scrollTo({
      top: Math.max(0, targetScrollY),
      behavior: 'smooth'
    })

    shadowLogger.debug(`Centered Shadow DOM container: viewport=${viewportHeight}px, container=${containerHeight}px, scrollTo=${Math.max(0, targetScrollY)}px`)
    
    // Call external callback if provided (for additional parent handling)
    if (this.onCenterRequest) {
      this.onCenterRequest(newHeight)
    }
  }



  /**
   * Mount React application in Shadow DOM
   */
  async mount(options?: ShadowMountOptions): Promise<void> {
    if (this.mounted) {
      shadowLogger.warn('ShadowManager already mounted')
      return
    }

    if (!this.shadowRoot) {
      throw new Error('Shadow DOM not initialized. Call init() first.')
    }

    try {
      // Import React mounter dynamically (same pattern as test SDK)
      const { ShadowReactMounter } = await import('./react-mounter')

      // Mount React app with resize handler
      await ShadowReactMounter.mount(this.shadowRoot, this.config, {
        onResize: this.handleResize, // Pass our internal handler
        onOpenLink: this.onOpenLink,
        onCenterRequest: this.handleCenterRequest // Pass our internal center handler
      })

      this.mounted = true

    } catch (error) {
      shadowLogger.error('❌ Failed to mount React app:', error)
      throw error
    }
  }

  /**
   * Destroy Shadow DOM and clean up resources
   */
  destroy(): void {
    try {
      // Unmount React app using ReactMounter
      if (this.shadowRoot && this.mounted) {
        import('./react-mounter').then(({ ShadowReactMounter }) => {
          ShadowReactMounter.unmount(this.shadowRoot!)
        }).catch(error => {
          shadowLogger.error('Error unmounting React app:', error)
        })
      }

      // Remove Shadow DOM
      if (this.container && this.shadowRoot) {
        this.container.removeChild(this.shadowRoot.host)
      }

      // Clear references
      this.shadowRoot = null
      this.container = null
      this.reactRoot = null
      this.mounted = false

    } catch (error) {
      shadowLogger.error('❌ Error during Shadow DOM cleanup:', error)
    }
  }

  /**
   * Update configuration (for dynamic updates)
   */
  updateConfig(newConfig: Partial<ShadowEmbedConfig>): void {
    const oldConfig = { ...this.config }
    this.config = { ...this.config, ...newConfig }
    shadowLogger.info('Shadow DOM config updated', newConfig)
    
    // Update custom CSS if it changed
    if (this.shadowRoot && newConfig.customCSS !== undefined) {
      ShadowCSSManager.updateCustomCSS(this.shadowRoot, newConfig.customCSS || '')
    }
    
    // TODO: Apply other config changes to mounted components
    // This could trigger re-renders or other updates
  }

  /**
   * Get current state information
   */
  getState() {
    return {
      mounted: this.mounted,
      hasShadowRoot: !!this.shadowRoot,
      hasContainer: !!this.container,
      hasReactRoot: !!this.reactRoot,
      config: this.config
    }
  }

  /**
   * Apply default styles to the widget container
   */
  private applyDefaultContainerStyles(container: HTMLElement): void {
    const isMobile = isMobileDevice()
    const shouldFloat = isMobile && this.config.mobileFloatingBar
    
    if (shouldFloat) {
      // Apply mobile floating styles
      this.applyMobileFloatingStyles(container)
    } else {
      // Default to inline/embedded positioning within content
      container.style.position = 'relative'
      container.style.width = this.config.width || '100%'
      // Remove fixed height - will be set dynamically
      container.style.minHeight = '100px' // Minimum height only
      container.style.margin = '20px 0'
      container.style.padding = '0'
      container.style.boxSizing = 'border-box'
      
      // Clean styling without borders or shadows
      container.style.borderRadius = '0'
      container.style.border = 'none'
      container.style.backgroundColor = 'transparent'
      container.style.boxShadow = 'none'
      
      // Responsive mobile styles
      if (isMobile) {
        container.style.width = '100%'
        container.style.minHeight = '80px' // Smaller minimum on mobile
        container.style.margin = '16px 0'
      }
    }
  }
  
  /**
   * Apply mobile floating bar styles
   */
  private applyMobileFloatingStyles(container: HTMLElement): void {
    // Use bottomOffset from config or default to '0'
    const bottomOffset = this.config.bottomOffset || '0'
    const defaultZIndex = 9999
    const defaultMaxHeight = '50vh'
    
    shadowLogger.info('📱 Applying mobile floating bar styles', { 
      bottomOffset,
      zIndex: defaultZIndex,
      maxHeight: defaultMaxHeight
    })
    
    // Fixed positioning at bottom of viewport
    container.style.position = 'fixed'
    container.style.bottom = bottomOffset
    container.style.left = '0'
    container.style.right = '0'
    container.style.top = 'auto'
    container.style.width = '100%'
    
    // Layer management
    container.style.zIndex = String(defaultZIndex)
    
    // Height constraints - always visible, can expand upward
    container.style.maxHeight = defaultMaxHeight
    container.style.minHeight = '80px' // Always show input bar
    container.style.height = 'auto' // Grow with content
    
    // Visual styling for floating bar
    // NO background - let the input itself have the white background
    container.style.backgroundColor = 'transparent'
    // Remove shadow from container - the input will have its own shadow
    container.style.boxShadow = 'none'
    container.style.borderRadius = '0' // No rounded corners on container
    container.style.padding = '0'
    container.style.margin = '0'
    container.style.boxSizing = 'border-box'
    
    // Ensure content inside doesn't add extra backgrounds
    container.style.isolation = 'isolate'
    
    // iOS safe area handling
    container.style.paddingBottom = 'env(safe-area-inset-bottom)'
    
    // Smooth transitions for height changes
    container.style.transition = 'height 0.3s ease-in-out, max-height 0.3s ease-in-out'
  }

  /**
   * Find a good insertion point within article content
   */
  private findArticleInsertionPoint(): Element | null {
    const { container: configContainer } = this.config
    
    // If a container is specified, try to use it
    if (configContainer && typeof configContainer === 'string') {
      const containerElement = document.querySelector(configContainer)
      if (containerElement) {
        // Try to find paragraphs within the specified container
        const paragraphs = containerElement.querySelectorAll('.text_editor_paragraph, p, .paragraph')
        if (paragraphs.length >= 2) {
          const insertAfter = paragraphs[1] // After 2nd paragraph
          shadowLogger.info(`📍 Found insertion point in specified container ${configContainer} after 2nd paragraph`)
          return insertAfter
        } else if (paragraphs.length === 1) {
          const insertAfter = paragraphs[0] // After 1st paragraph
          shadowLogger.info(`📍 Found insertion point in specified container ${configContainer} after 1st paragraph`)
          return insertAfter
        } else {
          // No paragraphs found, insert at the end of container
          shadowLogger.info(`📍 No paragraphs found in specified container ${configContainer}, will insert at end`)
          return containerElement
        }
      } else {
        shadowLogger.warn(`Specified container ${configContainer} not found, falling back to default logic`)
      }
    }
    
    // Fallback: ONLY TARGET: Insert after 2nd paragraph in ArticleBodyComponent
    const articleBody = document.querySelector('#ArticleBodyComponent')
    if (!articleBody) {
      throw new Error('ArticleBodyComponent not found - cannot insert widget')
    }
    
    const paragraphs = articleBody.querySelectorAll('.text_editor_paragraph')
    if (paragraphs.length < 2) {
      throw new Error('Not enough text_editor_paragraph elements in ArticleBodyComponent - cannot insert widget')
    }
    
    const insertAfter = paragraphs[1] // After 2nd paragraph
    shadowLogger.info('📍 Found ArticleBodyComponent insertion point after 2nd paragraph')
    return insertAfter
  }

  /**
   * Wait for container to appear with retries (for dynamically loaded content)
   */
  private async waitForContainer(selector: string, maxAttempts = 10, delay = 200): Promise<HTMLElement | null> {
    // First attempt - immediate check
    let element = document.querySelector(selector) as HTMLElement
    
    if (element) {
      shadowLogger.debug(`✅ Container found immediately: ${selector}`)
      return element
    }
    
    // Only retry if not found on first attempt
    shadowLogger.debug(`🔍 Container not found immediately, starting retry mechanism for: ${selector}`)
    
    for (let attempt = 2; attempt <= maxAttempts; attempt++) {
      await new Promise(resolve => setTimeout(resolve, delay))
      
      element = document.querySelector(selector) as HTMLElement
      
      shadowLogger.debug(`🔍 Container search attempt ${attempt}/${maxAttempts}`, {
        selector,
        found: !!element
      })
      
      if (element) {
        shadowLogger.info(`✅ Container found on attempt ${attempt}: ${selector}`)
        return element
      }
    }
    
    shadowLogger.warn(`❌ Container not found after ${maxAttempts} attempts: ${selector}`)
    return null
  }

  /**
   * Find suitable mount point for Shadow DOM
   * Based on config.container or auto-detection
   */
  private async findMountPoint(): Promise<HTMLElement | null> {
    const { container: configContainer, containerClass, mobileContainerClass } = this.config
    
    // Priority 1: Check for container classes (with mobile detection)
    const isMobile = isMobileDevice()
    const shouldFloat = isMobile && this.config.mobileFloatingBar
    
    // Special handling for mobile floating bar mode
    if (shouldFloat) {
      shadowLogger.info('📱 Mobile floating bar mode detected - creating fixed container')
      
      // Check if floating container already exists
      const existingContainer = document.getElementById(`kwry-floating-widget-${this.config.projectId}`)
      if (existingContainer) {
        shadowLogger.info('Using existing floating container')
        return existingContainer as HTMLElement
      }
      
      // Create a new floating container directly in body
      const floatingContainer = document.createElement('div')
      floatingContainer.id = `kwry-floating-widget-${this.config.projectId}`
      floatingContainer.className = 'kwry-floating-shadow-container'
      
      // Apply floating styles
      this.applyMobileFloatingStyles(floatingContainer)
      
      // Append to body for proper fixed positioning
      document.body.appendChild(floatingContainer)
      shadowLogger.info('✅ Mobile floating container created and attached to body')
      
      return floatingContainer
    }
    
    const activeContainerClass = isMobile && mobileContainerClass ? mobileContainerClass : containerClass
    
    if (activeContainerClass) {
      // Add debugging to see what's actually happening with the query
      shadowLogger.info('🔍 Direct query test for mainContent', {
        mainContentElements: document.querySelectorAll('.mainContent').length,
        firstElement: document.querySelector('.mainContent') ? {
          tagName: document.querySelector('.mainContent')!.tagName,
          className: document.querySelector('.mainContent')!.className,
          textContent: document.querySelector('.mainContent')!.textContent?.substring(0, 50)
        } : null,
        documentTitle: document.title,
        documentURL: document.URL
      })
      
      // Use retry mechanism for container detection
      const firstContainer = await this.waitForContainer(`.${activeContainerClass}`)
      if (firstContainer) {
        shadowLogger.info(`Using ${isMobile ? 'mobile' : 'desktop'} container class: ${activeContainerClass}`)
        
        // Check if widget already exists to prevent duplicates
        const existingWidget = document.getElementById(`kwry-widget-${this.config.projectId}`)
        if (existingWidget) {
          shadowLogger.warn(`Widget already exists for project ${this.config.projectId}, removing existing widget`)
          existingWidget.remove()
        }
        
        // Create a dedicated widget container inside the found container
        const widgetContainer = document.createElement('div')
        widgetContainer.id = `kwry-widget-${this.config.projectId}`
        widgetContainer.className = 'kwry-shadow-container'
        
        // Apply default widget positioning and dimensions
        this.applyDefaultContainerStyles(widgetContainer)
        
        // Insert based on widget position configuration
        this.insertWidgetContainer(firstContainer, widgetContainer, `container class: ${activeContainerClass}`)
        
        return widgetContainer
      }
      shadowLogger.warn(`No elements found with ${isMobile ? 'mobile' : 'desktop'} class '${activeContainerClass}', falling back to other methods`)
    }

    // Priority 2: Use specified container - create widget container inside it
    if (configContainer) {
      if (typeof configContainer === 'string') {
        const parentElement = await this.waitForContainer(configContainer)
        if (!parentElement) {
          shadowLogger.warn(`Container not found: ${configContainer}`)
          return null
        }
        
        // Check if widget already exists to prevent duplicates
        const existingWidget = document.getElementById(`kwry-widget-${this.config.projectId}`)
        if (existingWidget) {
          shadowLogger.warn(`Widget already exists for project ${this.config.projectId}, removing existing widget`)
          existingWidget.remove()
        }
        
        // Create a widget container inside the specified container
        const widgetContainer = document.createElement('div')
        widgetContainer.id = `kwry-widget-${this.config.projectId}`
        widgetContainer.className = 'kwry-shadow-container'
        
        // Apply default widget positioning and dimensions
        this.applyDefaultContainerStyles(widgetContainer)
        
        // Insert based on widget position configuration
        this.insertWidgetContainer(parentElement, widgetContainer, configContainer)
        
        return widgetContainer
      }
      
      if (configContainer instanceof HTMLElement) {
        // Same logic for HTMLElement - create widget container inside it
        const widgetContainer = document.createElement('div')
        widgetContainer.id = `kwry-widget-${this.config.projectId}`
        widgetContainer.className = 'kwry-shadow-container'
        
                 this.applyDefaultContainerStyles(widgetContainer)
         
        // Insert based on widget position configuration
        this.insertWidgetContainer(configContainer, widgetContainer, 'provided HTMLElement')
        
        return widgetContainer
      }
    }

    // Priority 3: Auto-detect - look for script tag with projectId
    const scripts = document.querySelectorAll('script[src*="kwry"]')
    for (const script of scripts) {
      const src = script.getAttribute('src') || ''
      if (src.includes(this.config.projectId)) {
        // Create container after the script
        const scriptContainer = document.createElement('div')
        scriptContainer.id = `kwry-widget-${this.config.projectId}`
        scriptContainer.className = 'kwry-shadow-container'
        
        // Apply default widget positioning and dimensions
        this.applyDefaultContainerStyles(scriptContainer)
        
        // Find a good insertion point within article content
        const insertionPoint = this.findArticleInsertionPoint()
        if (!insertionPoint) {
          throw new Error('No suitable article insertion point found - cannot insert widget')
        }
        
        this.insertWidgetInArticle(insertionPoint, scriptContainer)
        shadowLogger.info('✅ Widget inserted within article content')
        return scriptContainer
      }
    }

    // NO FALLBACKS - fail completely if script not found
    throw new Error('No matching script tag found for project ID: ' + this.config.projectId)
  }

  /**
   * Insert widget in article content based on position configuration
   */
  private insertWidgetInArticle(insertionPoint: Element, widgetContainer: HTMLElement): void {
    const position = this.config.widgetPosition || 'beginning' // Default to beginning
    
    if (position === 'end') {
      // For articles, 'end' means after more paragraphs (towards the end of content)
      // Try to find a later insertion point
      const articleBody = insertionPoint.closest('#ArticleBodyComponent')
      if (articleBody) {
        const allParagraphs = articleBody.querySelectorAll('.text_editor_paragraph, p, .paragraph')
        if (allParagraphs.length >= 4) {
          // Insert after the 4th paragraph (or closer to the end)
          const laterInsertionPoint = allParagraphs[3]
          laterInsertionPoint.parentNode?.insertBefore(widgetContainer, laterInsertionPoint.nextSibling)
          shadowLogger.info(`✅ Widget inserted at end position after 4th paragraph (total: ${allParagraphs.length}) (position: ${position})`)
          return
        } else if (allParagraphs.length === 3) {
          // If only 3 paragraphs, insert after the 3rd (last) one
          const lastParagraph = allParagraphs[2]
          lastParagraph.parentNode?.insertBefore(widgetContainer, lastParagraph.nextSibling)
          shadowLogger.info(`✅ Widget inserted at end position after 3rd (last) paragraph (position: ${position})`)
          return
        }
      }
      // Fallback: insert at the original point but log it as end positioning
      insertionPoint.parentNode?.insertBefore(widgetContainer, insertionPoint.nextSibling)
      shadowLogger.info(`✅ Widget inserted at end position (fallback to after 2nd paragraph) (position: ${position})`)
    } else {
      // Insert at the beginning position (after 2nd paragraph) - covers 'beginning' and 'mixed'
      insertionPoint.parentNode?.insertBefore(widgetContainer, insertionPoint.nextSibling)
      shadowLogger.info(`✅ Widget inserted at beginning position after 2nd paragraph (position: ${position})`)
    }
  }

  /**
   * Insert widget container based on position configuration
   */
  private insertWidgetContainer(parentElement: HTMLElement, widgetContainer: HTMLElement, containerDescription: string): void {
    const position = this.config.widgetPosition || 'beginning' // Default to beginning
    
    if (position === 'end') {
      // Insert at the end of the container
      parentElement.appendChild(widgetContainer)
      shadowLogger.info(`✅ Widget container inserted at end of ${containerDescription} (position: ${position})`)
    } else {
      // Insert at the beginning (default behavior) - covers 'beginning' and 'mixed'
      // For 'mixed', the positioning decision was already made by the backend
      if (parentElement.firstChild) {
        parentElement.insertBefore(widgetContainer, parentElement.firstChild)
        shadowLogger.info(`✅ Widget container inserted at beginning of ${containerDescription} (position: ${position})`)
      } else {
        parentElement.appendChild(widgetContainer)
        shadowLogger.info(`✅ Widget container appended to empty ${containerDescription} (position: ${position})`)
      }
    }
  }

}